<script setup>
import { computed, onBeforeUnmount, watch } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter } from 'dashboard/composables/store';
import { intlLocale, useLocoI18n } from 'custom/composables/useLocoI18n';
import { useAiSettings } from './useAiSettings';
import { useConversationAi } from './useConversationAi';
import { formatCost, formatTokens } from './helpers';

// In the conversation header, for admins: what AI cost in this conversation (assistant replies, agents' writing help
// and copilot, knowledge search, learning), refreshed as messages arrive
const props = defineProps({
  conversationId: { type: Number, required: true },
});

const REFRESH_DELAY_MS = 1500;

const { t, locale } = useLocoI18n();
const { isAdmin } = useAdmin();
const { isConfigured, load } = useAiSettings();
const { infoFor, refresh } = useConversationAi();
const currentChat = useMapGetter('getSelectedChat');

const usage = computed(() => infoFor(props.conversationId)?.usage);
const lastMessageId = computed(() => currentChat.value?.messages?.at(-1)?.id);
const money = value => formatCost(value, intlLocale(locale.value));

const details = computed(() =>
  [
    ...usage.value.by_kind.map(
      row =>
        `${t(`LOCO.AI.SETTINGS.KINDS.${row.kind.toUpperCase()}`)} ${money(row.cost)}`
    ),
    t('LOCO.AI.COST.TOKENS', {
      tokens: formatTokens(
        usage.value.input_tokens + usage.value.output_tokens,
        intlLocale(locale.value)
      ),
    }),
  ].join(' · ')
);

let timer = null;
watch(
  [() => props.conversationId, lastMessageId],
  async ([id], [previousId]) => {
    if (!isAdmin.value) return;
    await load();
    if (!isConfigured.value) return;
    clearTimeout(timer);
    if (id !== previousId) refresh(id);
    else timer = setTimeout(() => refresh(id), REFRESH_DELAY_MS);
  },
  { immediate: true }
);
onBeforeUnmount(() => clearTimeout(timer));
</script>

<template>
  <span
    v-if="isAdmin && usage?.calls"
    v-tooltip.bottom="t('LOCO.AI.COST.TOOLTIP', { details })"
    class="flex items-center gap-1 h-8 px-2 rounded-lg text-xs text-n-slate-11 bg-n-alpha-1"
  >
    <Icon icon="i-lucide-receipt" class="size-3.5" />
    {{ money(usage.cost) }}
  </span>
</template>
